import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTrigger,
  ChevronRightIcon,
} from '@noor/ui';
import { cn } from '@noor/lib';
import type { Branch } from '@/features/upsert-product';

interface BranchSubtitleProps {
  branchName: string;
  ariaLabel: string;
  branches: Branch[];
  activeBranchId: string | undefined;
  onSelect: (id: string) => void;
  /**
   * Label for an "all branches" entry above the list («Все филиалы»). Passed by
   * the Orders and Promotions screens; the Menu screen's mobile tree is
   * strictly branch-scoped. Mirrors `BranchSelector`'s own optional triple so
   * the two pickers offer the same choices.
   */
  allLabel?: string | undefined;
  allSelected?: boolean | undefined;
  onSelectAll?: (() => void) | undefined;
}

/**
 * The mobile Menu nav bar's subtitle (Figma node 26266:33057): the active
 * branch name with a trailing chevron, opening the branch picker. This is the
 * mobile equivalent of desktop's `BranchSelector` pill — same data, same
 * `useMerchantScope` wiring — but shaped to sit inline under the "Меню" title
 * instead of as its own toolbar row, because the mobile nav bar's subtitle
 * slot is where the design puts it.
 *
 * With fewer than two branches there is nothing to pick, so it renders as
 * plain text with no chevron — mirroring `BranchSelector`'s own fallback.
 */
export function BranchSubtitle({
  branchName,
  ariaLabel,
  branches,
  activeBranchId,
  onSelect,
  allLabel,
  allSelected,
  onSelectAll,
}: BranchSubtitleProps) {
  const showAll = allLabel !== undefined && onSelectAll !== undefined;
  // "All" is a second thing to pick, so it makes even a single-branch merchant's
  // subtitle interactive — same rule as `BranchSelector`.
  const isPickable = branches.length > 1 || (showAll && branches.length > 0);

  // Truncates the NAME only, in a `min-w-0` box so it actually has a bounded
  // width to shrink into (its flex ancestors, up through the nav bar's `<p>`,
  // are all `min-w-0` for exactly this) — the chevron sits outside that box so
  // it never gets clipped away by the name's ellipsis.
  const label = (
    <span className="flex min-w-0 items-center gap-1">
      <span className="min-w-0 truncate">
        {allSelected && showAll ? allLabel : branchName}
      </span>
      {isPickable && <ChevronRightIcon className="size-3.5 shrink-0 rotate-90" />}
    </span>
  );

  if (!isPickable) {
    return <span aria-label={ariaLabel}>{label}</span>;
  }

  return (
    <ActionMenu>
      <ActionMenuTrigger
        aria-label={ariaLabel}
        className={cn(
          'flex min-w-0 cursor-pointer items-center outline-none',
          'focus-visible:rounded-md focus-visible:ring-[3px] focus-visible:ring-ring/40',
        )}
      >
        {label}
      </ActionMenuTrigger>
      <ActionMenuContent align="center">
        {showAll ? (
          <ActionMenuItem
            onSelect={onSelectAll}
            className={cn(allSelected && 'font-medium')}
          >
            {allLabel}
          </ActionMenuItem>
        ) : null}
        {branches.map((branch) => (
          <ActionMenuItem
            key={branch.id}
            onSelect={() => onSelect(branch.id)}
            className={cn(!allSelected && branch.id === activeBranchId && 'font-medium')}
          >
            {branch.name}
          </ActionMenuItem>
        ))}
      </ActionMenuContent>
    </ActionMenu>
  );
}
